import { ref } from 'vue'
import { useUIStore } from '@/stores/ui'
import apiInterceptor from '@/shared/apiInterceptor'
import { environment } from '@/shared/env'

/**
 * Manages all import / export concerns:
 * - CSV import modal
 * - Import status modal + uploaded-file list
 * - Export customers (date-range modal from csvExportMixin, inlined here)
 * - Download failed-import CSV
 */
export function useImportExport() {
  const _uiStore = useUIStore()

  // ── Import ────────────────────────────────────────────────────────────────────
  const isOpenImportModal = ref(false)
  const isOpenImportStatusModal = ref(false)
  const uploadedListOfFiles = ref([])
  const uploadedFilesLoader = ref(false)

  // ── Export (inlined from csvExportMixin) ──────────────────────────────────────
  const csvExportWithDateRangeModal = ref(false)
  const csvExportWithDateRangeModal_type = ref('')
  const csvExportWithDateRangeModal_title = ref('')
  const downloadID = ref('')
  const fileName = ref('')

  // ── Private ───────────────────────────────────────────────────────────────────

  function _openExportModal(type, title, id = '', file = '') {
    csvExportWithDateRangeModal_type.value = type
    csvExportWithDateRangeModal_title.value = title
    downloadID.value = id
    fileName.value = file
    csvExportWithDateRangeModal.value = true
  }

  // ── Public ────────────────────────────────────────────────────────────────────

  async function fetchUploadedFilesList() {
    try {
      uploadedFilesLoader.value = true
      const response = await apiInterceptor.get(
        environment('VUE_APP_API_URL') + 'crm/v1/customers/import/',
      )
      uploadedListOfFiles.value = response.data
    } catch (error) {
      _uiStore.updateToast({ open: true, message: error.message, type: 'danger' })
    } finally {
      uploadedFilesLoader.value = false
    }
  }

  function openImportStatus() {
    isOpenImportStatusModal.value = true
    fetchUploadedFilesList()
  }

  function downloadFailedImport(id, file) {
    _openExportModal('FailedImports', 'Download Failed Customers', id, file)
    isOpenImportStatusModal.value = false
  }

  /**
   * Opens the customer export modal.
   *
   * The caller is responsible for confirming customers exist (via the loaded
   * list) before calling this. Gating on the loaded list rather than the count
   * text avoids a false "No customers yet!" — the count API
   * (crm/v1/customers/count) is slower than the list, so keying off the count
   * produced a false negative when the user clicked Export before it resolved.
   */
  function openExportCustomers() {
    _openExportModal('customers', 'Export customers')
  }

  /**
   * Shows the appropriate message when export isn't available yet: a "still
   * loading" hint while the count/stats request is in flight, otherwise the
   * genuine empty-state message.
   *
   * @param {object} opts
   * @param {boolean} opts.statsLoading - Whether the count/stats request is still in flight.
   */
  function notifyExportUnavailable({ statsLoading = false } = {}) {
    if (statsLoading) {
      _uiStore.updateToast({ open: true, message: 'Please wait, still loading customers…', type: 'warning' })
    } else {
      _uiStore.updateToast({ open: true, message: 'No customers yet!', type: 'danger' })
    }
  }

  return {
    isOpenImportModal,
    isOpenImportStatusModal,
    uploadedListOfFiles,
    uploadedFilesLoader,
    csvExportWithDateRangeModal,
    csvExportWithDateRangeModal_type,
    csvExportWithDateRangeModal_title,
    downloadID,
    fileName,
    openImportStatus,
    downloadFailedImport,
    openExportCustomers,
    notifyExportUnavailable,
  }
}
